import type { SupportedCountryCode } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  Columns,
  Column,
  ButtonIcon,
  IconClear,
  Box,
} from 'braid-design-system';
import { useState } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import { RightToWorkCertsyLink } from '../../RightToWorkCertsyLink/RightToWorkCertsyLink';
import type {
  DisplayedRightToWork,
  HandleUpdateRightsToWorkProps,
} from '../../types';

import translations from './.vocab';
import { RightToWorkCertsyBox } from './RightToWorkCertsyBox/RightToWorkCertsyBox';
import { RightToWorkDropdown } from './RightToWorkDropdown/RightToWorkDropdown';

interface ComponentProps {
  rightToWork: DisplayedRightToWork;
  handleUpdateRightsToWork: (ar0: HandleUpdateRightsToWorkProps) => void;
  rightToWorkRef?: React.RefObject<HTMLSelectElement>;
}

export const RightToWorkItem = ({
  rightToWork: {
    id,
    country: { name: label, countryCode, name: countryName },
    type2,
    verificationUrl,
    credential,
  },
  rightToWorkRef,
  handleUpdateRightsToWork,
}: ComponentProps) => {
  const { t } = useTranslations(translations);
  const { trackRightToWorkDeletePressed } = useAnalytics();

  const [dropdownValue, setDropdownValue] = useState(type2?.id);

  const actionName = type2?.formattedDescription ? 'EDIT' : 'ADD';

  const handleUpdateRightToWork = (rightToWorkOptionId: string) => {
    setDropdownValue(rightToWorkOptionId);
    handleUpdateRightsToWork({
      actionName: !rightToWorkOptionId ? 'DELETE' : actionName,
      country: { countryCode, name: countryName },
      type2: rightToWorkOptionId
        ? {
            id: rightToWorkOptionId,
          }
        : null,
      id,
    });
  };

  const onRemoveClick = () => {
    handleUpdateRightToWork('');
    trackRightToWorkDeletePressed();
  };

  const renderDropdownRightToWork = () => (
    <Stack space="small">
      <Columns space="small" alignY="center">
        <Column>
          <RightToWorkDropdown
            label={label}
            value={dropdownValue}
            rtwUpdate={handleUpdateRightToWork}
            rightToWorkRef={rightToWorkRef}
            countryCode={countryCode as SupportedCountryCode}
          />
        </Column>
        <Column width="content">
          <Box marginTop="small">
            <ButtonIcon
              id={`remove-rtw-button-${id}`}
              label={t('Remove')}
              variant="transparent"
              icon={<IconClear />}
              onClick={onRemoveClick}
              data={{ automation: `remove-rtw-button-${countryCode}` }}
            />
          </Box>
        </Column>
      </Columns>
      {verificationUrl ? (
        <Stack space="small">
          <Text>
            {t(
              'Confirming your right to work in Australia, whether as a visa...',
            )}
            <br />
          </Text>
          <RightToWorkCertsyLink
            linkAction="verify"
            linkUrl={verificationUrl}
          />
        </Stack>
      ) : null}
    </Stack>
  );

  return credential ? (
    <RightToWorkCertsyBox
      certsyRightToWork={credential}
      label={label}
      rightToWorkRef={rightToWorkRef}
    />
  ) : (
    renderDropdownRightToWork()
  );
};
